import * as THREE from 'three';
import {
	Camera,
	ShaderMaterial,
	ColorRepresentation,
	Euler,
	Fog,
	FogExp2,
	Group,
	Mesh,
	Object3D,
	OrthographicCamera,
	PerspectiveCamera,
	PMREMGenerator,
	SkeletonHelper,
	Texture,
	Vector2,
	Vector3,
	WebGLRenderer,
	Color,
	Material
} from 'three';
import {TransformControls} from "./controls/TransformControls.js";
import { Editor, EditorTheme } from './editor.js';
import {EditorControls} from './controls.js';
import { ViewHelper } from './viewhelper.js';
import { PandaOptions } from './index.js';
import {PandaMode} from './enums.js';
import { SetPositionCommand, SetRotationCommand, SetScaleCommand } from './commands/Commands.js';
import Stats from 'three/examples/jsm/libs/stats.module';
import * as TWEEN from "@tweenjs/tween.js";
import {ViewEffectComposer} from "./effect";

const MATERIAL_NORMAL = new THREE.MeshNormalMaterial( {
	// displacementMap: displacementMap,
	// displacementScale: SCALE,
	// displacementBias: BIAS,

	// normalMap: normalMap,
	normalScale: new THREE.Vector2( 1, - 1 ),

	flatShading: true,

	side: THREE.DoubleSide
} );

class Viewport{
	editor:Editor;
	dom:HTMLElement;
	camera:Camera;

	viewHelper:ViewHelper;
	controls: EditorControls;

	// object picking

	raycaster = new THREE.Raycaster();
	mouse = new THREE.Vector2();


	onDownPosition = new THREE.Vector2();
	onUpPosition = new THREE.Vector2();
	onDropPosition = new THREE.Vector2();
	onDoubleClickPosition = new THREE.Vector2();
	onContextmenuPosition = new THREE.Vector2();
	onMousemovePosition = new THREE.Vector2();
	isMouseOverModel = false;
	mouseOverModel = null;

	clock = new THREE.Clock(); // only used for animations
	//

	startTime = 0;
	endTime = 0;

	objects:Object3D[]=[];

	grid?:Group;
	renderer?:WebGLRenderer;
	mode:PandaMode=PandaMode.Edit;

	showSceneHelpers = true;

	options:PandaOptions;

	stats:Stats;
	multiObjects: Object3D[]=[];

	effectComposer: ViewEffectComposer

	private _fps:number = 60;
	private _timeStamp:number=0;
	private _singleFrameTime:number=(1/this._fps);
	set fps(fps:number){
		this._fps=fps;
		this._singleFrameTime=1/fps;
	}
	constructor( editor:Editor,options:PandaOptions ) {
		this.editor=editor;
		this.options=options;
		const signals = editor.signals;

		this.dom=document.createElement('div');
		this.dom.className="panda-viewport";
		this.dom.style.position='relative';
		this.dom.style.width='100%';
		this.dom.style.height='100%';
		this.dom.style.outline='none';
		this.dom.setAttribute('tabindex','0');

		this.stats=new Stats();
		this.stats.dom.style.position='absolute';
		this.stats.dom.style.top='0';
		this.stats.dom.style.left='0';
		this.stats.dom.style.zIndex='auto';
		this.stats.dom.style.display='none';

		//
		var pmremGenerator:PMREMGenerator;

		var camera = editor.camera;
		this.camera=camera;
		var scene = editor.scene;
		var sceneHelpers = editor.sceneHelpers;

		this.viewHelper = new ViewHelper( camera, this.dom );

		// helpers
		var grid = new THREE.Group();

		var grid1 = new THREE.GridHelper( 1000, 600, 'rgb(225, 225, 225)');//0x888888
		grid1.material.color.setHex( 0xb4b4b4 );
		grid1.material.vertexColors = false;
		grid.add( grid1 );

		var grid2 = new THREE.GridHelper( 1000, 300, 'rgb(180, 180, 180)' );//0x222222
		grid2.material.color.setHex( 0x404147 );
		grid2.material.depthFunc = THREE.AlwaysDepth;
		grid2.material.vertexColors = false;
		grid.add( grid2 );

		this.grid=grid;

		var box = new THREE.Box3();

		var selectionBox = new THREE.BoxHelper(new Object3D());
		selectionBox.material.depthTest = false;
		selectionBox.material.transparent = true;
		if(this.options?.selectColor){
			selectionBox.material.color=new THREE.Color(this.options?.selectColor);
		}
		selectionBox.visible = false;
		sceneHelpers.add( selectionBox );

		var objectPositionOnDown:Vector3|undefined;
		var objectRotationOnDown:Euler|undefined;
		var objectScaleOnDown:Vector3|undefined;

		var transformControls = new TransformControls( camera, this.dom );
		transformControls.addEventListener( 'change',  () =>{

			var object = transformControls.object;

			if ( object !== undefined ) {

				selectionBox.setFromObject( object );

				var helper = editor.helpers[ object.id ];

				if ( helper !== undefined && helper instanceof SkeletonHelper) {

					helper.update();

				}

				signals.refreshSidebarObject3D.dispatch( object );

			}

			this.render();

		} );
		transformControls.addEventListener( 'mouseDown',  () =>{

			var object = transformControls.object;

			objectPositionOnDown = object?.position.clone();
			objectRotationOnDown = object?.rotation.clone();
			objectScaleOnDown = object?.scale.clone();

			this.controls.controls.enabled = false;

		} );
		transformControls.addEventListener( 'mouseUp',  () =>{

			var object = transformControls.object;

			if ( object !== undefined ) {

				switch ( transformControls.getMode() ) {

					case 'translate':

						if ( ! objectPositionOnDown?.equals( object.position ) ) {

							editor.execute( new SetPositionCommand( editor, object, object.position, objectPositionOnDown ) );

						}

						break;

					case 'rotate':

						if ( ! objectRotationOnDown?.equals( object.rotation ) ) {

							editor.execute( new SetRotationCommand( editor, object, object.rotation, objectRotationOnDown ) );

						}

						break;

					case 'scale':

						if ( ! objectScaleOnDown?.equals( object.scale ) ) {

							editor.execute( new SetScaleCommand( editor, object, object.scale, objectScaleOnDown ) );

						}

						break;

				}

			}

			this.controls.controls.enabled = true;

		} );

		sceneHelpers.add( transformControls );


		// events

		this.dom.addEventListener( 'mousedown', this.onMouseDown.bind(this), false );   //鼠标左键点击事件
		this.dom.addEventListener( 'touchstart', this.onTouchStart.bind(this), false ); //触摸事件
		this.dom.addEventListener( 'dblclick', this.onDoubleClick.bind(this), false );	 // 鼠标左键双击

		// controls need to be added *after* main logic,
		// otherwise controls.enabled doesn't work.

		this.controls = new EditorControls( this.camera, this.dom);
		this.controls.setOptions(this.options.control)
		this.controls.controls.addEventListener( 'change',  ()=>{
			this.editor.signals.cameraChanged.dispatch( camera );
			this.editor.signals.refreshSidebarObject3D.dispatch( camera );
		} );

		this.viewHelper.center = this.controls.controls.target;
		if(this.mode!==PandaMode.Edit){
			this.viewHelper.destroy();
		}

		// signals

		signals.editorCleared.add( ()=> {
			this.controls.controls.target.set( 0, 0, 0 );
			this.render(true);

		} );

		signals.transformModeChanged.add(  ( mode:"translate" | "rotate" | "scale" )=> {
			transformControls.setMode( mode );
			this.render(true);
		} );

		signals.snapChanged.add(  ( dist:number|null )=> {
			transformControls.setTranslationSnap( dist );
		} );

		signals.spaceChanged.add( ( space:"world" | "local" ) =>{
			transformControls.setSpace( space );
		} );

		signals.rendererUpdated.add(  (animate?:boolean) =>{
			if(animate===true){
				if(this.viewHelper&&this.mode!==PandaMode.Edit){
					this.viewHelper.destroy();
				}else{
					this.viewHelper=new ViewHelper(this.camera,this.dom);
				}
				this.renderer?.setAnimationLoop( this.animate.bind(this) );
			}

			scene.traverse( function ( child ) {

				if ( child instanceof Mesh && child.material !== undefined ) {

					(child.material as Material).needsUpdate = true;

				}

			} );

			this.render();

		} );
		// 渲染器创建完成
		signals.rendererCreated.add( ( newRenderer:WebGLRenderer ) =>{
			if ( this.renderer ) {
				this.renderer.setAnimationLoop( null );
				this.renderer.dispose();
				pmremGenerator.dispose();

				this.dom.removeChild( this.renderer.domElement );

			}

			this.renderer = newRenderer;

			this.renderer?.setAnimationLoop( this.animate.bind(this) );

			if ( window.matchMedia&&this.editor.theme===EditorTheme.System ) {

				var mediaQuery = window.matchMedia( '(prefers-color-scheme: dark)' );
				mediaQuery.addListener(  ( event ) =>{

					this.renderer?.setClearColor( event.matches ? 0x333333 : 0xd2d3d6 );//0xaaaaaa

					this.updateGridColors( this.grid.children[0] as Mesh, this.grid.children[1] as Mesh, event.matches ? [ 0x222222, 0x888888 ] : [ 0x888888, 0x282828 ] );

					this.render();

				} );

				this.renderer?.setClearColor( mediaQuery.matches ? 0x333333 : 0xd2d3d6 );
				this.updateGridColors(this.grid.children[0] as Mesh, this.grid.children[1] as Mesh, mediaQuery.matches ? [ 0x222222, 0x888888 ] : [ 0x888888, 0x282828 ] );

			}else if(this.editor.theme===EditorTheme.Dark){
				this.renderer?.setClearColor( 0x333333 );
				this.updateGridColors(this.grid.children[0] as Mesh, this.grid.children[1] as Mesh, [ 0x222222, 0x888888 ] );
			}else{
				this.renderer?.setClearColor( 0xd2d3d6 );
				this.updateGridColors(this.grid.children[0] as Mesh, this.grid.children[1] as Mesh, [ 0x888888, 0x282828 ] );
			}

			this.renderer?.setPixelRatio( window.devicePixelRatio );
			this.renderer?.setSize( this.dom.offsetWidth, this.dom.offsetHeight );
			// this.renderer=renderer;

			if(this.renderer){
				pmremGenerator = new THREE.PMREMGenerator( this.renderer );
				pmremGenerator.compileEquirectangularShader();

				this.dom.appendChild( this.renderer.domElement );
			}


			this.render(true);

		} );

		signals.sceneGraphChanged.add(  (force?:boolean)=> {

			this.render(force);

		} );

		signals.cameraChanged.add(  ()=> {

			this.render();

		} );

		signals.objectSelected.add(  ( object:Object3D|null ) =>{

			selectionBox.visible = false;
			transformControls.detach();

			if ( object !== null && object !== scene && object !== camera ) {

				box.setFromObject( object );

				if ( box.isEmpty() === false ) {

					selectionBox.setFromObject( object );
					selectionBox.visible = true;

				}

				transformControls.attach( object );

			}

			this.render(true);

		} );

		signals.objectFocused.add(  ( object:Object3D|null)=> {
			if(object){
				this.controls.focus(object)
			}
		} );

		signals.geometryChanged.add(  ( object?:Object3D ) =>{

			if ( object !== undefined ) {

				selectionBox.setFromObject( object );

			}

			this.render();

		} );

		signals.objectAdded.add(  ( object:Object3D )=> {

			object.traverse(  ( child ) =>{

				this.objects.push( child );

			} );

		} );

		signals.objectChanged.add(  ( object:Object3D ) =>{

			if ( editor.selected === object ) {

				selectionBox.setFromObject( object );

			}

			if ( object instanceof PerspectiveCamera) {

				object.updateProjectionMatrix();

			}

			if ( editor.helpers[ object.id ] !== undefined ) {

				(editor.helpers[ object.id ] as SkeletonHelper)?.update();

			}

			this.render(true);

		} );

		signals.objectRemoved.add(  ( object:Object3D ) =>{

			this.controls.controls.enabled = true; // see #14180
			if ( object === transformControls.object ) {

				transformControls.detach();

			}

			object.traverse(  ( child )=> {

				this.objects.splice( this.objects.indexOf( child ), 1 );

			} );

		} );

		signals.helperAdded.add(  ( object:Object3D ) =>{

			var picker = object.getObjectByName( 'picker' );

			if ( picker !== undefined ) {

				this.objects.push( picker );

			}

		} );

		signals.helperRemoved.add(  ( object:Object3D ) =>{

			var picker = object.getObjectByName( 'picker' );

			if ( picker !== undefined ) {

				this.objects.splice( this.objects.indexOf( picker ), 1 );

			}

		} );

		signals.materialChanged.add(  ()=> {

			this.render(true);

		} );

		// signals.animationStopped.add( function () {

		// 	this.render();

		// } );

		// background
		signals.sceneBackgroundChanged.add( (background:null|Color|Texture )=> {
			//THREE.EquirectangularReflectionMapping
			scene.background=background;
			this.render();

		} );

		// environment
		signals.sceneEnvironmentChanged.add(  ( environmentEquirectangularTexture:Texture|null )=> {
			this.editor.scene.environment = pmremGenerator.fromEquirectangular( environmentEquirectangularTexture ).texture;
			this.render();
		} );

		// fog
		signals.sceneFogChanged.add(  ( fogType:"None"|"Fog"|"FogExp2", fogColor:ColorRepresentation, fogNear:number | undefined, fogFar:number | undefined, fogDensity:number | undefined )=>{

			switch ( fogType ) {

				case 'None':
					scene.fog = null;
					break;
				case 'Fog':
					scene.fog = new THREE.Fog( fogColor, fogNear, fogFar );
					break;
				case 'FogExp2':
					scene.fog = new THREE.FogExp2( fogColor, fogDensity );
					break;

			}

			this.render();

		} );

		signals.sceneFogSettingsChanged.add(  ( fogType:"None"|"Fog"|"FogExp2", fogColor:number, fogNear:number, fogFar:number, fogDensity:number )=> {
			if(scene.fog){
				if(scene.fog instanceof Fog){
					scene.fog.color.setHex( fogColor );
					scene.fog.near = fogNear;
					scene.fog.far = fogFar;
				}else if(scene.fog instanceof FogExp2){
					scene.fog.color.setHex( fogColor );
					scene.fog.density = fogDensity;
				}
			}


			this.render();

		} );
		
		signals.viewportCameraChanged.add(  () =>{

			let viewportCamera = editor.viewportCamera;
			console.info("摄像头切换事件",viewportCamera)
			if ( viewportCamera instanceof PerspectiveCamera) {

				viewportCamera.aspect = (editor.camera as PerspectiveCamera).aspect;
				viewportCamera.projectionMatrix.copy( editor.camera.projectionMatrix );

			} else if ( viewportCamera instanceof OrthographicCamera) {
				viewportCamera.left = - (editor.camera as PerspectiveCamera).aspect;;
				viewportCamera.right = (editor.camera as PerspectiveCamera).aspect;;
			}

			// disable EditorControls when setting a user camera
			const enabled=( viewportCamera === editor.camera );
			this.controls.controls.enabled =enabled;
			this.showSceneHelpers=enabled;

			this.render(true);

		} );

		signals.exitedVR.add( this.render );

		//

		signals.windowResize.add( ()=>{

			this.updateAspectRatio();

			this.renderer?.setSize( this.dom.offsetWidth, this.dom.offsetHeight );

			this.render();

		} );

		signals.showGridChanged.add(  ( showGrid:boolean )=> {

			grid.visible = showGrid;
			this.render();

		} );

		signals.showHelpersChanged.add(  ( showHelpers:boolean ) =>{

			this.showSceneHelpers = showHelpers;
			transformControls.enabled = showHelpers;

			this.render();

		} );

		signals.cameraResetted.add( this.updateAspectRatio.bind(this) );

		signals.viewportShadingChanged.add(()=>{
			this.editor.scene.traverse((child)=>{

					if (child instanceof Mesh) {
						if(this.editor.viewportShading==='wireframe'){
							if(child.userData._normals){
								child.material=child.userData._normals;
							}
							child.material.wireframe = true;
						}else if(this.editor.viewportShading==='normals'){
							child.material.wireframe = false;
							child.userData._normals=child.material;
							child.material=MATERIAL_NORMAL.clone();
						}else{
							child.material.wireframe = false;
							if(child.userData._normals){
								child.material=child.userData._normals;
							}
						}

					}
			});
			this.render(true);
		});
	}

	updateAspectRatio() {
		if(this.camera!==undefined&&this.camera instanceof PerspectiveCamera){
			this.camera.aspect = this.dom.offsetWidth / this.dom.offsetHeight;
			this.camera.updateProjectionMatrix();
		}
	}

	getIntersects( point:Vector2, objects:Object3D[] ) {
		this.mouse.set( ( point.x * 2 ) - 1, - ( point.y * 2 ) + 1 );
		this.raycaster.setFromCamera( this.mouse, this.camera );
		const selectableObjects = objects.filter(function (child) {
			return child.userData.selectable !== false;
		});
		return this.raycaster.intersectObjects( selectableObjects );

	}

	getMousePosition( dom:HTMLElement, x:number, y:number ) {

		var rect = dom.getBoundingClientRect();
		return [ ( x - rect.left ) / rect.width, ( y - rect.top ) / rect.height ];

	}

	handleClick(event) {

		if ( this.onDownPosition.distanceTo( this.onUpPosition ) === 0 ) {
			this.onDownPosition=new Vector2();

			//let intersects = this.getIntersects( this.onUpPosition, this.objects );
			let intersects = this.getIntersects( this.onUpPosition, this.editor.scene.children );
			if ( intersects.length > 0 ) {
				let object = intersects[ 0 ].object;
				if(event.ctrlKey){
					if(object === undefined) return
					while (object.parent.type !== "Scene") {
						object = object.parent;
					}
					if (this.multiObjects.includes(object)) return;
					this.editor.ctrlSelectObject[object.id] = object.parent.id
					this.multiObjects.push(object)
					this.editor.selectAll( [...this.multiObjects] );
				}else{
					this.editor.cancelMulti()
					//向上遍历获取最高层级为组
					while (object.parent.type !== "Scene") {
						object = object.parent;
					}

					this.multiObjects = [object]
					this.editor.ctrlSelectObject[object.id] = object.parent === null ? this.editor.scene.id : object.parent.id
					this.editor.select( object );
				}
			} else {
				this.editor.select(  );
			}

			this.render(true);

		}

	}

	onMouseDown( event:MouseEvent ) {
		event.stopPropagation();
		event.preventDefault();

		var array = this.getMousePosition( this.dom, event.clientX, event.clientY );
		this.onDownPosition.fromArray( array );

		document.addEventListener( 'mouseup', this.onMouseUp.bind(this), false );

	}

	onMouseUp( event:MouseEvent ) {
		event.stopPropagation();

		var array = this.getMousePosition( this.dom, event.clientX, event.clientY );
		this.onUpPosition.fromArray( array );

		this.handleClick(event);
		document.removeEventListener( 'mouseup', this.onMouseUp.bind(this), false );
	}

	onTouchStart( event:TouchEvent ) {
		event.stopPropagation();
		var touch = event.changedTouches[ 0 ];

		var array = this.getMousePosition( this.dom, touch.clientX, touch.clientY );
		this.onDownPosition.fromArray( array );

		document.addEventListener( 'touchend', this.onTouchEnd.bind(this), false );

	}

	onTouchEnd( event:TouchEvent ) {

		var touch = event.changedTouches[ 0 ];

		var array = this.getMousePosition( this.dom, touch.clientX, touch.clientY );
		this.onUpPosition.fromArray( array );

		this.handleClick(event);

		document.removeEventListener( 'touchend', this.onTouchEnd.bind(this), false );

	}

	onDoubleClick( event:MouseEvent ) {

		var array = this.getMousePosition( this.dom, event.clientX, event.clientY );
		this.onDoubleClickPosition.fromArray( array );

		var intersects = this.getIntersects( this.onDoubleClickPosition, this.objects );

		if ( intersects.length > 0 ) {

			var intersect = intersects[ 0 ];

			this.editor.signals.objectFocused.dispatch( intersect.object );
			this.editor.signals.objectDblclick.dispatch( intersect.object );
		}
	}


	// animations
	animate() {
		// 物体看向摄像头
		this.editor.objLookCamera(this.editor.scene,this.editor.camera)

		this.controls.controls.update()

		var mixer = this.editor.mixer;
		var delta = this.clock.getDelta();
		var needsUpdate = true;
		mixer.update( delta );

		this.stats.update();

		if ( this.mode===PandaMode.Edit && this.viewHelper.animating === true ) {
			needsUpdate = true;
		}
		if ( needsUpdate === true ){
			this.render();//渲染
		}

	}

	render(force?:boolean) {
		if(force!==true){
			var delta = this.clock.getDelta();
			this._timeStamp += delta;
			if (this._timeStamp <= this._singleFrameTime) {
				return;
			}
			this._timeStamp = (this._timeStamp % this._singleFrameTime);
		}

		this.startTime = performance.now();

		// Adding/removing grid to scene so materials with depthWrite false
		// don't render under the grid.
		if(this.grid){
			this.editor.scene.add( this.grid );
		}
		this.renderer?.setViewport( 0, 0, this.dom.offsetWidth, this.dom.offsetHeight );
		// 是后期处理时使用的 todo
		this.editor.effectComposer.effectComposer.render();
		//this.renderer?.render( this.editor.scene, this.editor.viewportCamera );
		if(this.grid){
			this.editor.scene.remove( this.grid );
		}

		if ( this.camera === this.editor.viewportCamera && this.renderer) {
			this.renderer.autoClear = false;
			if ( this.showSceneHelpers === true ) this.renderer.render( this.editor.sceneHelpers, this.camera );
			if(this.mode===PandaMode.Edit){
				this.viewHelper.render( this.renderer );
			}
			this.renderer.autoClear = true;
		}

		this.endTime = performance.now();
		this.editor.signals.sceneRendered.dispatch( this.endTime - this.startTime );

	}

	updateGridColors( grid1:Mesh, grid2:Mesh, colors:number[] ) {
		const material=grid1.material;
		if(material&&material instanceof THREE.MeshBasicMaterial){
			material.color.setHex( colors[ 0 ] );
			material.color.setHex( colors[ 1 ] );
		}
		if(grid2.material&&grid2.material instanceof THREE.MeshBasicMaterial){
			grid2.material.color.setHex( colors[ 0 ] );
			grid2.material.color.setHex( colors[ 1 ] );
		}

	}

}


export { Viewport };
